import { NotesWithMentionsDto } from './notes.models';

export enum TimeOffType {
  Vacation = 'Vacation',
  Sick = 'Sick',
  VacationCarryOver = 'Vacation Carry-Over',
  OptionalHoliday = 'Optional Holiday',
}

/**
 * Manager-side notes for a time off request, split by status transition.
 * Each slot is populated independently.
 */
export interface TimeOffActionNotesDto {
  approval: NotesWithMentionsDto | null;
  return: NotesWithMentionsDto | null;
  cancellation: NotesWithMentionsDto | null;
}

export const TimeOffTypeCodes = {
  VACATION: 'VACATION',
  SICK: 'SICK',
  BEREAVEMENT: 'BEREAVEMENT',
} as const;

/**
 * URL fragment that asks the office details page to open its Practice Area Overrides section and
 * scroll to it. Lives here rather than on either side so the link that emits it and the page that
 * honours it cannot drift apart — a typo would otherwise fail silently, leaving the admin on a
 * page with the section still shut.
 */
export const PRACTICE_AREA_OVERRIDES_FRAGMENT = 'practice-area-overrides';

export interface TimeOffEntry {
  id: number;
  displayStartDate: string | null; // ISO date string
  displayEndDate: string | null; // ISO date string
  type: TimeOffType | string;
  daysOff: number;
  typeLookupValueId?: number;
  typeCode?: string; // Type code from API (e.g., VACATION, SICK)
  statusCode?: string; // Status code from API (e.g., APPROVED, PENDING)
  days?: string[]; // Individual days within the time off request
  carryOverDays?: string[]; // Subset of `days` that drew from vacation carry-over (PBI 5770 AC#3)
  hasNotes?: boolean;
  hasManagerNotes?: boolean;
  notesInfo?: NotesWithMentionsDto | null;
}

export interface ResourceTimeOff {
  employeeId: string;
  resourceName: string;
  entries: TimeOffEntry[];
}

export interface TimeOffDialogData {
  resourceName: string;
  employeeId: string;
  entries: TimeOffEntry[];
  lookupTypes: TimeOffTypeLookup[];
}

export interface TimeOffDialogResult {
  entries: TimeOffEntry[];
}

// API DTOs
export interface TimeOffRequestDto {
  id: number;
  employeeId: string;
  typeLookupValueId: number;
  typeName: string;
  typeCode: string;
  statusLookupValueId: number;
  statusName: string;
  statusCode: string;
  displayStartDate: string | null;
  displayEndDate: string | null;
  daysOff: number;
  days: string[]; // ISO date strings (yyyy-MM-dd)
  carryOverDays?: string[]; // Subset of `days` that drew from vacation carry-over (PBI 5770 AC#3)
  hasNotes: boolean;
  hasManagerNotes?: boolean;
  notesInfo?: NotesWithMentionsDto | null;
  createdAt: string;
  updatedAt: string | null;
  isActive: boolean;
}

export interface CreateTimeOffRequestRequest {
  employeeId: string;
  typeLookupValueId: number;
  startDate: string;
  endDate: string;
}

/**
 * Adjusts which days a request covers (PUT time-off/{id}/days and time-off/my/{id}/days). `days`
 * is the complete set of calendar days the request should cover afterwards (yyyy-MM-dd, sorted),
 * exactly as the server stores them: weekends when selected, and holidays wherever they fall
 * inside a resulting run, plus a holiday the stored request began or ended on while that edge is
 * unchanged. The picker never shows holidays, so the dialog puts them back when it builds this
 * payload; the server splits the set on strict calendar adjacency, so a holiday left out would
 * split the request. Every resulting request keeps the original type.
 */
export interface UpdateTimeOffRequestRequest {
  days: string[];
}

/** What PUT time-off/{id}/days and time-off/my/{id}/days return. */
export interface TimeOffRequestAdjustmentResultDto {
  /** The original request after the change: the run that kept its id, notes and approval. */
  updated: TimeOffRequestDto;
  /** Requests a split created, in date order; empty when the days stayed contiguous. */
  created: TimeOffRequestDto[];
  removedDays: string[]; // yyyy-MM-dd
  addedDays: string[]; // yyyy-MM-dd
}

export interface TimeOffTypeLookup {
  id: number;
  code: string;
  name: string;
  sortOrder: number;
  isDefault: boolean;
}

export type TimeOffApiMode = 'admin' | 'self-service';

// Time Off Policy By Office
export interface TimeOffOfficePolicyDto {
  id: number;
  officeCode: string;
  typeLookupValueId: number;
  typeName: string;
  typeCode: string;
  defaultDays: number;
  maxCarryoverDays: number | null;
  /**
   * null = the office's default policy for the type. A code = that practice area's override
   * within the office, which replaces the office row outright (both defaultDays and
   * maxCarryoverDays) for resources whose primary practice area matches. Vacation only.
   */
  practiceAreaCode: string | null;
  createdAt: string;
  updatedAt: string | null;
  isActive: boolean;
}

export interface CreateTimeOffOfficePolicyRequest {
  officeCode: string;
  typeLookupValueId: number;
  defaultDays: number;
  maxCarryoverDays?: number | null;
  /** Omit for the office default; set to write a practice area's Vacation override. */
  practiceAreaCode?: string | null;
}

export interface UpdateTimeOffOfficePolicyRequest {
  officeCode: string;
  typeLookupValueId: number;
  defaultDays: number;
  maxCarryoverDays?: number | null;
  /**
   * Must match the stored row: the API rejects any change to the (office, type, practice area)
   * identity with a 400. Send the override's code when editing an override; omit it when editing
   * the office default.
   */
  practiceAreaCode?: string | null;
}

export interface TimeOffResourcePolicyDto {
  id: number;
  employeeId: string;
  typeLookupValueId: number;
  typeName: string;
  typeCode: string;
  defaultDays: number;
  maxCarryoverDays: number | null;
  /**
   * Legacy manual carry-over override. Read-only: it can no longer be set via the
   * create/update requests (Task 6666), but any pre-existing value is still surfaced.
   */
  carryoverDaysOverride: number | null;
  /**
   * First year this policy row applies to (inclusive start — a row for 2027 applies to 2027
   * and later, until a row with a greater effectiveYear takes over). Null is the baseline row
   * that applies to any year no dated row covers.
   */
  effectiveYear: number | null;
  createdAt: string;
  updatedAt: string | null;
  isActive: boolean;
}

export interface CreateTimeOffResourcePolicyRequest {
  employeeId: string;
  typeLookupValueId: number;
  defaultDays: number;
  maxCarryoverDays?: number | null;
  effectiveYear?: number | null;
}

export interface UpdateTimeOffResourcePolicyRequest {
  employeeId: string;
  typeLookupValueId: number;
  defaultDays: number;
  maxCarryoverDays?: number | null;
  effectiveYear?: number | null;
}

export interface TimeOffBalanceDto {
  employeeId: string;
  year: number;
  officeCode: string;
  country: string | null;
  hireDate: string | null;
  /**
   * True when this year's carry-over was projected from the prior year's live balance because the
   * Jan 1 job has not materialized the year yet (next year only). The figures move as the
   * prior year's time off changes and nothing is persisted; the sheet is labelled "Projected".
   */
  isProjected: boolean;
  /**
   * The server's current (UTC) year when the balance was computed. The sheet bounds its year
   * navigation and its edit affordance on this rather than the browser clock, which can sit on a
   * different year for a few hours around New Year.
   */
  serverYear: number;
  balances: TimeOffTypeBalanceDto[];
}

export interface TimeOffTypeBalanceDto {
  typeCode: string;
  typeName: string;
  defaultEntitled: number;
  proratedEntitled: number;
  carryoverDays: number;
  /**
   * Of `carryoverDays`, the window-exempt LOA carry-over credit (vacation only, granted April 1 for
   * a Q1 Leave of Absence). Not genuine carry-over: it is not bound by the April 1 deadline and
   * survives through Dec 31. Zero for sick and for vacation with no credit.
   */
  carryoverCredit: number;
  totalAvailable: number;
  daysTaken: number;
  holidayOverlap: number;
  adjustedDaysTaken: number;
  /** How many of the taken days drew from carry-over (vacation deducts from carry-over first). */
  carryoverDaysUsed: number;
  remaining: number;
  /**
   * The entitlement the resource inherits before any resource-level override: their practice
   * area's Vacation override for their office when one is in force, otherwise the office default.
   * Named "office…" from before practice-area overrides existed; see `practiceAreaCode`.
   */
  officeDefaultEntitledDays: number;
  /** The inherited carry-over cap, resolved the same way; null = no cap. */
  officeMaxCarryoverDays: number | null;
  /**
   * Which policy produced the two inherited figures above: the practice area whose override is
   * in force for this resource, or null when they came from the office default. Anything that
   * attributes those numbers to a source ("your office allows N days") must branch on this.
   */
  practiceAreaCode: string | null;
}

export interface TimeOffResourceOverrideDto {
  employeeId: string;
  employeeName: string;
  typeCode: string;
  typeName: string;
  defaultDays: number;
  carryoverDaysAmount: number | null;
  effectiveYear: number | null;
  practiceAreaCodes: string[];
}

// Time Off History (finalized past-year snapshots)

export interface TimeOffHistoryDto {
  employeeId: string;
  year: number;
  /** Empty when no history has been materialized for the year (API returns 200, not 404). */
  types: TimeOffTypeHistoryDto[];
}

export interface TimeOffTypeHistoryDto {
  typeCode: string;
  typeName: string;
  entitled: number;
  taken: number;
  remaining: number;
}

// Vacation carry-over (carry-over + Q1 leave-of-absence credit for a year)

export interface VacationCarryOverDto {
  employeeId: string;
  year: number;
  carryOver: number;
  carryOverCredit: number;
}

/**
 * Sets an employee's absolute current-year available balance for a type (current year only),
 * replacing the total rather than adding to it. The server derives the entitlement from its own
 * current carry-over, so the resulting total matches `availableDays`. (PBI 5770)
 */
export interface SetCurrentYearAvailableRequest {
  employeeId: string;
  year: number;
  typeLookupValueId: number;
  availableDays: number;
}

// Time off overlap check (GET api/timeoff/overlap-check, approvers only)

/**
 * A matter the requester and an overlapping resource are both assigned to. The names come from the
 * external client-matter service and are null when it did not answer for the number, so the
 * number is always there to show.
 */
export interface TimeOffOverlapMatterDto {
  clientMatterNumber: string;
  clientName: string | null;
  matterName: string | null;
}

/**
 * One time off request of another resource that overlaps the checked range. A resource with two
 * overlapping requests appears twice: status is a fact of the request, and an approver weighs a
 * pending one differently from an approved one.
 */
export interface TimeOffOverlapDto {
  timeOffRequestId: number;
  employeeId: string;
  /** "First Last", or the employee id when the sync cache has no row for them. */
  employeeName: string;
  /** PENDING or APPROVED; no other status counts as an overlap. */
  statusCode: string;
  statusName: string;
  /** The working days both ranges share (yyyy-MM-dd, ascending); weekends and holidays never appear. */
  days: string[];
  /** Every matter the two share, ordered by number. */
  matters: TimeOffOverlapMatterDto[];
}

/**
 * What GET api/timeoff/overlap-check answers: whether another resource with the same primary
 * practice area, assigned to a matter the requester is also on, already holds pending or approved
 * time off on a working day of the range, and when so, who and on which matters.
 */
export interface TimeOffOverlapCheckResultDto {
  hasOverlap: boolean;
  /** One entry per overlapping request, ordered by last name, first name, then first shared day. */
  overlaps: TimeOffOverlapDto[];
}
